/**
 * @fileoverview Hook for managing core story structure settings.
 *
 * Provides read/write access to StoryStructure fields like POV, tense,
 * genre, detailed plot, and other narrative configuration.
 */

'use client';

import { useCallback, useMemo } from 'react';
import { useProject } from '@/context/project-context';
import { useToast } from '@/hooks/use-toast';
import { StoryStructure } from '../schemas';
import { updateStoryStructure as updateStoryStructureOnServer } from '../services';

export function useStructure() {
    const { project, setProject } = useProject();
    const { toast } = useToast();

    // Correctly derive storyStructure from the project context on every render.
    // This ensures the hook always has the latest state.
    const storyStructure = useMemo(() => project.storyStructure, [project.storyStructure]);

    const updateStructure = useCallback(async (updates: Partial<StoryStructure>) => {
        // Capture only storyStructure for a surgical rollback — restoring the
        // whole project would destroy any concurrent state changes.
        let previousStoryStructure: StoryStructure | null = null;

        // Use functional update to avoid stale closure race conditions
        setProject(prev => {
            previousStoryStructure = prev.storyStructure;
            return {
                ...prev,
                storyStructure: { ...prev.storyStructure, ...updates },
                updatedAt: new Date().toISOString(),
            };
        });

        try {
            await updateStoryStructureOnServer(project.id, updates);
        } catch (error) {
            // Roll back only storyStructure, preserving any concurrent changes
            // to other parts of the project.
            if (previousStoryStructure) {
                setProject(prev => ({ ...prev, storyStructure: previousStoryStructure! }));
            }
            toast({
                variant: 'destructive',
                title: 'Save Failed',
                description: 'Could not update story structure.'
            });
            throw error;
        }
    }, [project.id, setProject, toast]);

    return {
        storyStructure,
        updateStructure,
    };
}
